import { computed, inject, type ComputedRef, type InjectionKey, type Ref } from 'vue'

/**
 * Field helper interface returned by createFieldHelper
 */
interface FieldHelper {
    invalid: ComputedRef<boolean>
    error: ComputedRef<string>
    clear: () => void
}

/**
 * Validation instance interface as provided by SectionedForm
 */
interface ValidationInstance {
    errors: Ref<Record<string, string>>
    isValid: ComputedRef<boolean>
    validate: () => boolean
    createFieldHelper: (fieldName: string) => FieldHelper
    reset: () => void
}

/**
 * Injection key for validation instance
 * Using Symbol.for() to match the key provided in SectionedForm.vue
 */
const VALIDATION_KEY: InjectionKey<ComputedRef<ValidationInstance | null>> =
    Symbol.for('validation')

/**
 * Return type of useFieldError composable
 */
interface UseFieldErrorReturn {
    /**
     * Whether the field has a validation error
     */
    invalid: ComputedRef<boolean>
    /**
     * The error message for the field, or empty string if no error
     */
    error: ComputedRef<string>
    /**
     * Clear the validation error for this field (immutable update)
     */
    clear: () => void
}

/**
 * Composable for accessing field-level validation errors.
 * Provides reactive access to validation state for a specific field.
 *
 * @param fieldName - The name of the field to validate
 * @returns Object with invalid state, error message, and clear function
 *
 * @example
 * ```typescript
 * const emailField = useFieldError('email')
 *
 * // In template:
 * <InputText
 *   :invalid="emailField.invalid.value"
 *   @update:modelValue="emailField.clear()"
 * />
 * <small v-if="emailField.error.value">
 *   {{ emailField.error.value }}
 * </small>
 * ```
 */
export function useFieldError(fieldName: string): UseFieldErrorReturn {
    const validation = inject(
        VALIDATION_KEY,
        computed(() => null)
    )

    return {
        /**
         * Whether the field has a validation error
         */
        invalid: computed(() => !!validation.value?.errors.value[fieldName]),

        /**
         * The error message for the field, or empty string if no error
         */
        error: computed(() => validation.value?.errors.value[fieldName] || ''),

        /**
         * Clear the validation error for this field (immutable update)
         */
        clear: () => {
            if (validation.value?.errors.value[fieldName]) {
                const { [fieldName]: _, ...rest } = validation.value.errors.value
                validation.value.errors.value = rest
            }
        },
    }
}
